/**
 * Small presentational bits shared across the CRM list surfaces (lead inbox,
 * kanban cards, conversation rows): per-channel icon and study chips.
 */
import { Activity, Mail, MessageSquare, Phone, StickyNote } from 'lucide-react';
import { StudyLink } from '../types';
import { prettySlug } from '../lib';

export function ChannelIcon({ channel }: { channel: string }) {
  const cls = 'h-3.5 w-3.5 flex-shrink-0';
  switch (channel) {
    case 'sms': return <MessageSquare className={cls} />;
    case 'call': return <Phone className={cls} />;
    case 'note': return <StickyNote className={cls} />;
    case 'email': return <Mail className={cls} />;
    default: return <Activity className={cls} />;
  }
}

const BADGE_YELLOW = 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300';
const BADGE_RED = 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
const BADGE_GREEN = 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
const BADGE_GRAY = 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';

/**
 * Canonical landing-form prescreen verdict badge (the forms-engine
 * `evaluateEligibility` outcome persisted on `crm.contact_leads.eligibility_status`:
 * eligible | needs_review | not_eligible | pending). Single source of truth shared
 * by the lead-inbox study chips, the profile ScreenerPanel, and the CommsThread
 * questionnaire card so all three render the SAME wording + colors for the same data.
 *
 * Returns `null` for an empty/missing status so callers that want names-only
 * (inbox chips) can skip the badge; callers that always want a badge (the profile
 * panel) pass `incomplete` and/or treat null as "Incomplete screener" themselves.
 */
export function eligibilityBadge(status: string | null): { label: string; cls: string } | null {
  if (status === 'pending') return { label: 'Incomplete screener', cls: BADGE_YELLOW };
  const s = (status || '').toLowerCase();
  if (!s) return null;
  // Humanize raw values ("not_eligible" -> "Not Eligible") to match the other badges.
  const label = prettySlug(status);
  // Order matters: "not_eligible" CONTAINS the substring "elig", so the negative
  // cases must be matched BEFORE the positive "elig" check — otherwise an
  // ineligible lead is mislabeled green — and "needs_review" must map to yellow.
  if (s.includes('not_elig') || s.includes('inelig') || s === 'fail' || s === 'failed' || s === 'disqualified') {
    return { label, cls: BADGE_RED };
  }
  if (s.includes('review') || s.includes('flag')) {
    return { label, cls: BADGE_YELLOW };
  }
  if (s.includes('elig') || s === 'pass' || s === 'qualified') {
    return { label, cls: BADGE_GREEN };
  }
  return { label, cls: BADGE_GRAY };
}

/**
 * Highlight an individual answer that drove the eligibility outcome:
 * 'exclude' (disqualifying) → red, 'review' (flagged) → yellow, else the
 * default answer text color. Shared by ScreenerPanel, FormResponseModal and
 * SendFormCard so a flagged answer looks the same on every surface.
 */
export function answerHighlight(kind: 'exclude' | 'review' | null | undefined): string {
  if (kind === 'exclude') return 'rounded-md bg-red-100 px-1.5 py-0.5 text-red-800 dark:bg-red-900/40 dark:text-red-300';
  if (kind === 'review') return 'rounded-md bg-yellow-100 px-1.5 py-0.5 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300';
  return 'text-gray-900 dark:text-gray-100';
}

/**
 * Fold a response's `eligibility_reasons` (what the forms engine recorded at
 * completion: one entry per triggered rule) into question_key → kind for
 * per-answer highlighting. A question whose exclude AND review rules both
 * fired gets 'exclude' — the stronger verdict wins.
 */
export function reasonKinds(
  reasons: { question_key: string; kind: string }[] | null | undefined
): Map<string, 'exclude' | 'review'> {
  const m = new Map<string, 'exclude' | 'review'>();
  // jsonb column — tolerate a malformed value rather than crash the modal.
  for (const r of Array.isArray(reasons) ? reasons : []) {
    if (r.kind === 'exclude') m.set(r.question_key, 'exclude');
    else if (r.kind === 'review' && m.get(r.question_key) !== 'exclude') m.set(r.question_key, 'review');
  }
  return m;
}

/** Up-to-two study chips with a +N overflow chip; full list in the tooltip.
 *  Each chip appends the study's landing-form prescreen verdict (when present)
 *  via the shared `eligibilityBadge` helper. */
export function StudyChips({ studies, max = 2 }: { studies: StudyLink[] | undefined; max?: number }) {
  if (!studies || studies.length === 0) return <span className="text-sm text-gray-400">—</span>;
  const shown = studies.slice(0, max);
  const extra = studies.length - shown.length;
  // Recruiter view: prefer the study's internal alias, fall back to the public
  // title frozen on the lead snapshot, then a prettified slug.
  const names = studies.map((s) => s.study_internal_title || s.study_title || prettySlug(s.study_slug));
  return (
    <span className="flex flex-wrap items-center gap-1" title={names.join(', ')}>
      {shown.map((s, i) => {
        const name = s.study_internal_title || s.study_title || prettySlug(s.study_slug);
        const badge = eligibilityBadge(s.eligibility_status);
        return (
          <span key={i} className="inline-flex items-center gap-1">
            <span className="max-w-[10rem] truncate rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-300">
              {name}
            </span>
            {badge && (
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${badge.cls}`}>{badge.label}</span>
            )}
          </span>
        );
      })}
      {extra > 0 && (
        <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-500 dark:bg-gray-700 dark:text-gray-400">+{extra}</span>
      )}
    </span>
  );
}
